import React, { useState, useEffect, useMemo, useCallback, forwardRef, useImperativeHandle } from 'react';
import { DIALOG_TITLE_SX } from '../utils/dialogStyle';
import {
    Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Typography, Grid,
    IconButton, Alert, CircularProgress, MenuItem, Chip, Tooltip,
    ButtonBase,
} from '@mui/material';
import {
    Add, Close, Edit, Delete, PlayArrow, Pause, Stop, Update, ExpandMore, ExpandLess, Remove,
    History as HistoryIcon,
} from '@mui/icons-material';
import { businessAPI } from '../api';
import AddPlansDialog from './AddPlansDialog';

/**
 * A customer's plans — the one piece shared by the Plans page (inside each customer card) and
 * the Customers tab (a customer's plans). Both screens show exactly this, so they cannot drift
 * apart: plan lines with Pause / Change Qty / End and their scheduled "Pausing…" / "Ending…"
 * chips, Earlier plans, each product's folded History as date ranges, and Edit for the
 * individual changes — with every dialog those need, including Add plan.
 *
 * Give it the customer's plan entries (`logs`); it works out what is current from them.
 * `onChanged` is called after any save so the parent can reload (quietly — no page spinner).
 * Call `ref.current.openAdd()` to open Add plan from outside (the Plans card's + button).
 */

// ── Shared helpers ───────────────────────────────────────────────────────────
export const QTY_ACTIONS = ['Start', 'Resume', 'Change in Quantity'];   // quantity means something
const ACTIVE_ACTIONS = ['Start', 'Resume', 'Change in Quantity'];
const ACTION_DISPLAY = { Suspend: 'Pause', 'Change in Quantity': 'Change Qty' };
// Vendors say "restart", not "resume". (Stored actions keep their names — only the words change.)
const ACTION_LABEL = { pause: 'Pause', resume: 'Restart', end: 'End', changeQty: 'Change Quantity' };
const ACTION_DONE = { pause: 'paused', resume: 'restarted', end: 'ended', changeQty: 'updated' };
const ACTION_MAP = { pause: 'Suspend', resume: 'Resume', end: 'End', changeQty: 'Change in Quantity' };

const getActionColor = (action) => ({
    'Start': 'success', 'Resume': 'info', 'Change in Quantity': 'primary', 'Suspend': 'warning', 'End': 'error',
}[action] || 'default');
const isActivePlan = (action) => ACTIVE_ACTIONS.includes(action);
// `products` includes deleted ones, so a customer's old plan still shows what it was.
export const getProductName = (products, id) => {
    const p = products.find(x => x.f_id === id);
    if (!p) return 'Unknown';
    return p.f_is_archived ? `${p.f_name} (deleted)` : (p.f_name || 'Unknown');
};

// Warning after a plan change that reaches into closed months: the change is saved, but those
// months' bills only pick it up once the vendor reopens them and makes the bills again.
export const closedMonthsNote = (affected) => {
    if (!affected?.length) return '';
    const names = affected.map(p => p.period_display).join(', ');
    const one = affected.length === 1;
    return ` ⚠️ ${names} bills are closed. Reopen ${one ? 'that month' : 'those months'} to update ${one ? 'its' : 'their'} bills.`;
};

const formatError = (err) => {
    if (Array.isArray(err.response?.data?.detail))
        return err.response.data.detail.map(e => e.msg || JSON.stringify(e)).join(', ');
    return err.response?.data?.detail || err.message || 'An error occurred';
};

// Dates as local YYYY-MM-DD — toISOString() would give yesterday before 5:30 AM IST.
export const toISODate = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};
export const addDaysISO = (iso, n) => { const d = new Date(iso + 'T00:00:00'); d.setDate(d.getDate() + n); return toISODate(d); };
const daysBetweenISO = (a, b) => Math.round((new Date(b + 'T00:00:00') - new Date(a + 'T00:00:00')) / 86400000);
export const fmtDay = (iso) => {
    if (!iso) return '';
    try { return new Date(iso + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }); }
    catch { return iso; }
};
export const fmtDayY = (iso) => {
    if (!iso) return '';
    try { return new Date(iso + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }); }
    catch { return iso; }
};
const fmtRange = (from, to) => {
    if (from === to) return fmtDayY(from);
    if (from.slice(0, 7) === to.slice(0, 7)) return `${Number(from.slice(8))} – ${fmtDayY(to)}`;   // 1 – 5 Nov 2026
    if (from.slice(0, 4) === to.slice(0, 4)) return `${fmtDay(from)} – ${fmtDayY(to)}`;
    return `${fmtDayY(from)} – ${fmtDayY(to)}`;
};
export const fmtQty = (q) => { const n = Number(q); return Number.isFinite(n) ? String(Number(n.toFixed(3))) : String(q ?? ''); };
// "2 items per day" / "1 item per day" — plain words for vendors, not "2 a day" or "2/day".
// A plan on set days reads "1 item · Sundays".
export const itemsText = (q, scheduleLabel) => {
    const items = `${fmtQty(q)} ${Number(q) === 1 ? 'item' : 'items'}`;
    return scheduleLabel && scheduleLabel !== 'Every day' ? `${items} · ${scheduleLabel}` : `${items} per day`;
};

const byDateAsc = (a, b) =>
    (a.f_effective_from_date || '').localeCompare(b.f_effective_from_date || '')
    || (a.f_inserted_at || '').localeCompare(b.f_inserted_at || '');

/**
 * What a customer's plans look like today, from their plan entries.
 * Current state per product = the latest entry effective on or before today — not the latest
 * of all: a future End or Pause must not change what shows today (a plan ending next month is
 * still running now). Plans starting in the future show as upcoming. Entries after today are
 * attached as scheduledResume / scheduledEnd / scheduledPause.
 * Returns { subscriptions, history } — history newest first.
 */
export const buildCustomerPlanState = (logs) => {
    const today = toISODate(new Date());
    const byProduct = {};
    (logs || []).forEach(l => { (byProduct[l.f_business_product_id] ||= []).push(l); });
    const subscriptions = [];
    Object.values(byProduct).forEach(list => {
        const asc = [...list].sort(byDateAsc);
        let current = null, currentIdx = -1;
        for (let i = 0; i < asc.length; i++) {
            if (asc[i].f_effective_from_date <= today) { current = asc[i]; currentIdx = i; } else break;
        }
        const repr = current || asc[0];
        if (!repr || repr.f_action === 'End') return;
        const future = asc.slice(currentIdx + 1);
        const extra = {};
        if (repr.f_action === 'Suspend') {
            const r = future.find(l => l.f_action === 'Resume');
            if (r) extra.scheduledResume = r;
        }
        const end = future.find(l => l.f_action === 'End');
        if (end) extra.scheduledEnd = end;
        if (repr.f_action !== 'Suspend') {
            const pIdx = future.findIndex(l => l.f_action === 'Suspend');
            if (pIdx !== -1) {
                extra.scheduledPause = {
                    pauseLog: future[pIdx],
                    resumeLog: future.slice(pIdx + 1).find(l => l.f_action === 'Resume') || null,
                };
            }
        }
        subscriptions.push(Object.keys(extra).length ? { ...repr, ...extra } : repr);
    });
    const history = [...(logs || [])].sort((a, b) => byDateAsc(b, a));
    return { subscriptions, history };
};

// Pair each Pause with the following Resume for the same product so a fixed-length pause reads
// as one row. An unpaired Pause is open-ended ("until started again"). Newest first.
const pairHistoryLogs = (logs) => {
    const asc = [...logs].sort(byDateAsc);
    const used = new Set();
    const items = [];
    asc.forEach((log, i) => {
        if (used.has(log.f_id) || log.f_action !== 'Suspend') return;
        used.add(log.f_id);
        const resume = asc.find((x, j) =>
            j > i && x.f_action === 'Resume' && !used.has(x.f_id)
            && x.f_business_product_id === log.f_business_product_id);
        if (resume) used.add(resume.f_id);
        items.push({ kind: 'pause', pauseLog: log, resumeLog: resume || null });
    });
    asc.forEach(log => { if (!used.has(log.f_id)) items.push({ kind: 'single', log }); });
    items.sort((a, b) => {
        const ad = a.kind === 'pause' ? a.pauseLog.f_effective_from_date : a.log.f_effective_from_date;
        const bd = b.kind === 'pause' ? b.pauseLog.f_effective_from_date : b.log.f_effective_from_date;
        return (bd || '').localeCompare(ad || '');
    });
    return items;
};

// ── Plan history as date ranges ─────────────────────────────────────────────
// Vendors read "what was this customer getting, and when", so a product's history is shown as
// ranges ("1 Jan – 28 Feb 2026 · 2 items per day"). Same date rules as billing:
//   Start / Resume / Change Qty: delivered from that day · Pause: paused from that day
//   End: that day is the LAST delivery day; the gap until a later Start reads "Stopped"
export const buildPlanRanges = (logs, scheduleLabel) => {
    const asc = [...logs].sort(byDateAsc);
    const today = toISODate(new Date());
    const ranges = [];
    asc.forEach((e, i) => {
        const next = asc[i + 1];
        const from = e.f_effective_from_date;
        if (e.f_action === 'End') {
            const gapFrom = addDaysISO(from, 1);
            if (next) {
                const gapTo = addDaysISO(next.f_effective_from_date, -1);
                if (gapTo >= gapFrom) ranges.push({ from: gapFrom, to: gapTo, kind: 'stopped', label: 'Stopped' });
            } else {
                ranges.push({ from: gapFrom, to: null, kind: 'stopped', label: 'Stopped' });
            }
            return;
        }
        const to = next ? (next.f_action === 'End' ? next.f_effective_from_date : addDaysISO(next.f_effective_from_date, -1)) : null;
        if (to && to < from) return;
        ranges.push(e.f_action === 'Suspend'
            ? { from, to, kind: 'paused', label: 'Paused' }
            : { from, to, kind: 'active', label: itemsText(e.f_quantity, scheduleLabel) });
    });
    return ranges.map(r => ({
        ...r,
        planned: r.from > today,
        when: r.to ? fmtRange(r.from, r.to)
            : r.from > today ? `from ${fmtDayY(r.from)}`
            : r.kind === 'stopped' ? `from ${fmtDayY(r.from)}`
            : `${fmtDayY(r.from)} – today`,
    }));
};

// Folded history for one product: its ranges, plus an Edit button for the individual changes.
function PlanHistory({ ranges, onFix }) {
    const colour = { active: 'success.main', paused: 'warning.main', stopped: 'text.disabled' };
    return (
        <Box sx={{ mt: 0.75, pl: 1, borderLeft: '2px solid', borderColor: 'divider' }}>
            {ranges.map((r, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'baseline', gap: 1, py: 0.25 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: colour[r.kind], flexShrink: 0, alignSelf: 'center' }} />
                    <Typography variant="body2" sx={{ minWidth: 0, flex: 1 }}>{r.when}</Typography>
                    <Typography variant="body2" fontWeight={700} sx={{ flexShrink: 0, textAlign: 'right' }}
                        color={r.kind === 'active' ? 'text.primary' : colour[r.kind]}>
                        {r.label}{r.planned ? ' (planned)' : ''}
                    </Typography>
                </Box>
            ))}
            {/* A plain, familiar Edit — opens the individual changes behind these dates */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                <Button size="small" variant="outlined" startIcon={<Edit sx={{ fontSize: '1rem !important' }} />}
                    onClick={onFix}
                    sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 5, minHeight: 32, px: 1.5 }}>
                    Edit
                </Button>
            </Box>
        </Box>
    );
}

const chipSx = { height: 24, fontSize: '0.7rem', cursor: 'pointer', '& .MuiChip-icon': { ml: 0.5 } };

// ── The shared component ───────────────────────────────────────────────────
const CustomerPlans = forwardRef(function CustomerPlans({
    businessId, customer, logs, products, onChanged, expanded, onToggleExpand,
}, ref) {
    const { subscriptions, history } = useMemo(() => buildCustomerPlanState(logs), [logs]);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const refresh = useCallback(async () => { if (onChanged) await onChanged(); }, [onChanged]);

    // History open/closed per product. On the Plans page the card decides (one card open at a
    // time); on the Customers tab there is just one customer, so it is always allowed.
    const controlled = expanded !== undefined;
    const [openHistory, setOpenHistory] = useState(() => new Set());
    const isOpen = (pid) => (controlled ? expanded : true) && openHistory.has(pid);
    const toggleHistory = (pid) => {
        if (controlled && !expanded) {
            onToggleExpand && onToggleExpand(customer.f_id);
            setOpenHistory(new Set([pid]));
            return;
        }
        setOpenHistory(prev => { const n = new Set(prev); if (n.has(pid)) n.delete(pid); else n.add(pid); return n; });
    };

    const logsByProduct = useMemo(() => {
        const m = {};
        for (const l of history) (m[l.f_business_product_id] ||= []).push(l);
        return m;
    }, [history]);
    const scheduleOf = (pid) => products.find(p => p.f_id === pid)?.schedule_label;
    const currentIds = new Set(subscriptions.map(s => s.f_business_product_id));
    const earlierIds = Object.keys(logsByProduct).map(Number).filter(pid => !currentIds.has(pid));

    // ── Edit list (the individual changes behind one product's dates) ──
    const [fixProductId, setFixProductId] = useState(null);
    const [fixError, setFixError] = useState('');
    const fixItems = useMemo(
        () => (fixProductId ? pairHistoryLogs(logsByProduct[fixProductId] || []) : []),
        [fixProductId, logsByProduct],
    );

    // ── Dialog state ──
    const [addOpen, setAddOpen] = useState(false);
    // After the Add plans picker closes: "2 plans added", plus any closed-month warning
    const [notice, setNotice] = useState('');
    useEffect(() => {
        if (!notice) return undefined;
        const t = setTimeout(() => setNotice(''), 6000);
        return () => clearTimeout(t);
    }, [notice]);
    const [actionDialog, setActionDialog] = useState({ open: false, type: null, log: null });
    const [actionForm, setActionForm] = useState({});
    const [editingLog, setEditingLog] = useState(null);
    const [editForm, setEditForm] = useState({});

    const resetMessages = () => { setError(''); setSuccess(''); };
    const finish = (msg, close) => {
        setSuccess(msg);
        setTimeout(() => { close(); setSuccess(''); }, 1200);
    };
    const mkLog = (log, action, date, qty, reason = null) => ({
        f_business_id: businessId,
        f_business_customer_id: customer.f_id,
        f_business_product_id: log.f_business_product_id,
        f_quantity: qty,
        f_effective_from_date: date,
        f_updated_by: parseInt(localStorage.getItem('user_id')),
        f_action: action,
        f_reason: reason,
    });

    // ── Add plans (a picker of every plan-able product, several at once) ──
    const openAdd = () => { resetMessages(); setAddOpen(true); };
    useImperativeHandle(ref, () => ({ openAdd }));
    const handleAdded = async (affected, keepOpen) => {
        await refresh();
        if (!keepOpen) {
            setAddOpen(false);
            setNotice('Plans added.' + closedMonthsNote(affected));
        }
    };

    // ── Pause / Resume / End / Change Qty ──
    const closeAction = () => setActionDialog({ open: false, type: null, log: null });
    const openAction = (type, log) => {
        const today = toISODate(new Date());
        // Pause defaults to tomorrow (a plan usually stops from the next day). "Resume now" on a
        // plan with a scheduled comeback brings that resume forward instead of adding a second.
        const rescheduleResume = type === 'resume' && log.scheduledResume;
        const base = (type === 'pause' || rescheduleResume) ? addDaysISO(today, 1) : today;
        setActionDialog({ open: true, type, log, ...(rescheduleResume ? { replaceResumeId: log.scheduledResume.f_id } : {}) });
        setActionForm({ f_effective_from_date: base, f_quantity: log.f_quantity, duration: 'forever', endDate: addDaysISO(base, 2) });
        resetMessages();
    };
    // Editing a pause reopens the pause dialog pre-filled; Save replaces the old Pause (+Resume).
    const openPauseEdit = (item) => {
        const { pauseLog, resumeLog } = item;
        let duration = 'forever';
        let endDate = addDaysISO(pauseLog.f_effective_from_date, 2);
        if (resumeLog) {
            const d = daysBetweenISO(pauseLog.f_effective_from_date, resumeLog.f_effective_from_date);
            if (d >= 1 && d <= 7) duration = String(d);
            else { duration = 'until'; endDate = resumeLog.f_effective_from_date; }
        }
        // Resume first: the server refuses to delete a Pause while its Resume still follows it.
        setActionDialog({ open: true, type: 'pause', log: pauseLog, editIds: [resumeLog?.f_id, pauseLog.f_id].filter(Boolean) });
        setActionForm({ f_effective_from_date: pauseLog.f_effective_from_date, f_quantity: pauseLog.f_quantity, duration, endDate });
        resetMessages();
    };
    // "Ending <date>": change the date (Save replaces the queued End) or Keep Plan (removes it).
    const openEndEdit = (log) => {
        setActionDialog({ open: true, type: 'end', log, editEndId: log.scheduledEnd.f_id });
        setActionForm({ f_effective_from_date: log.scheduledEnd.f_effective_from_date, f_quantity: log.f_quantity,
            duration: 'forever', endDate: addDaysISO(log.scheduledEnd.f_effective_from_date, 2) });
        resetMessages();
    };
    const removeEntries = async (ids, msg) => {
        setBusy(true);
        setError('');
        try {
            for (const id of ids) { await businessAPI.deleteCustomerSubscription(id); }
            await refresh();
            finish(msg, closeAction);
        } catch (err) {
            setError(formatError(err));
            await refresh();
        } finally {
            setBusy(false);
        }
    };
    const handleAction = async () => {
        const { type, log, editIds, replaceResumeId, editEndId } = actionDialog;
        setError('');
        if (type === 'pause') {
            const pauseDate = actionForm.f_effective_from_date;
            let resumeDate = null;
            if (actionForm.duration === 'until') resumeDate = actionForm.endDate;
            else if (actionForm.duration !== 'forever') resumeDate = addDaysISO(pauseDate, Number(actionForm.duration));
            if (resumeDate && resumeDate <= pauseDate) { setError('Please pick a resume date after the pause date.'); return; }
            setBusy(true);
            try {
                if (editIds && editIds.length) {
                    for (const id of editIds) { await businessAPI.deleteCustomerSubscription(id); }
                }
                const sRes = await businessAPI.createCustomerSubscription(mkLog(log, 'Suspend', pauseDate, log.f_quantity, 'Self'));
                let ap = sRes.data.affected_locked_periods || [];
                if (resumeDate) {
                    try {
                        const rRes = await businessAPI.createCustomerSubscription(mkLog(log, 'Resume', resumeDate, log.f_quantity));
                        ap = ap.concat(rRes.data.affected_locked_periods || []);
                    } catch (e) {
                        // Roll back the pause so we never leave a dangling open-ended pause.
                        const sid = sRes.data?.f_id;
                        if (sid) { try { await businessAPI.deleteCustomerSubscription(sid); } catch { /* ignore */ } }
                        throw e;
                    }
                }
                await refresh();
                finish((resumeDate ? 'Pause scheduled!' : 'Plan paused!') + closedMonthsNote(ap), closeAction);
            } catch (err) {
                setError(formatError(err));
                await refresh();   // an edit may have removed the old pair before failing
            } finally {
                setBusy(false);
            }
            return;
        }
        setBusy(true);
        const rescheduling = type === 'resume' && !!replaceResumeId;
        const replacingEnd = type === 'end' && !!editEndId;
        try {
            if (rescheduling) await businessAPI.deleteCustomerSubscription(replaceResumeId);
            if (replacingEnd) await businessAPI.deleteCustomerSubscription(editEndId);
            const res = await businessAPI.createCustomerSubscription(mkLog(
                log, ACTION_MAP[type], actionForm.f_effective_from_date,
                type === 'changeQty' ? actionForm.f_quantity : log.f_quantity, type === 'end' ? 'Self' : null));
            const verb = ACTION_DONE[type] || 'updated';
            await refresh();
            finish((replacingEnd ? 'End date updated!' : `Plan ${verb}!`) + closedMonthsNote(res.data.affected_locked_periods), closeAction);
        } catch (err) {
            setError(formatError(err));
            if (rescheduling || replacingEnd) await refresh();
        } finally {
            setBusy(false);
        }
    };

    // ── Edit one change / delete ──
    const openEdit = (log) => {
        setFixProductId(null);
        setEditingLog(log);
        setEditForm({ f_quantity: log.f_quantity, f_effective_from_date: log.f_effective_from_date, f_reason: log.f_reason || '' });
        resetMessages();
    };
    const handleEdit = async () => {
        setError('');
        setBusy(true);
        try {
            // Only the details of an entry are editable — not what happened (its action).
            const res = await businessAPI.updateCustomerSubscription(editingLog.f_id, {
                f_effective_from_date: editForm.f_effective_from_date,
                ...(QTY_ACTIONS.includes(editingLog.f_action) ? { f_quantity: editForm.f_quantity } : {}),
                f_reason: editForm.f_reason || null,
            });
            await refresh();
            finish('Change saved!' + closedMonthsNote(res.data.affected_locked_periods), () => setEditingLog(null));
        } catch (err) {
            setError(formatError(err));
        } finally {
            setBusy(false);
        }
    };
    // Deletes from the Edit list show their result inside that list (its dialog covers the page).
    const deleteFromList = async (ids, question) => {
        if (!window.confirm(question)) return;
        setFixError('');
        try {
            for (const id of ids) { await businessAPI.deleteCustomerSubscription(id); }
            await refresh();
        } catch (err) {
            setFixError(formatError(err));
            await refresh();
        }
    };

    // ── Render ──
    const historyLink = (pid) => (logsByProduct[pid]?.length > 0) && (
        <ButtonBase onClick={() => toggleHistory(pid)}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, py: 0.25, borderRadius: 1,
                  color: isOpen(pid) ? 'primary.main' : 'text.secondary', typography: 'caption', fontWeight: 700 }}>
            <HistoryIcon sx={{ fontSize: 15 }} />
            History
            {isOpen(pid) ? <ExpandLess sx={{ fontSize: 16 }} /> : <ExpandMore sx={{ fontSize: 16 }} />}
        </ButtonBase>
    );
    const historyBody = (pid) => isOpen(pid) && (
        <PlanHistory ranges={buildPlanRanges(logsByProduct[pid] || [], scheduleOf(pid))} onFix={() => { setFixError(''); setFixProductId(pid); }} />
    );

    const qtyButtons = (setForm, value) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
            <IconButton size="small" onClick={() => setForm(d => ({ ...d, f_quantity: Math.max(1, d.f_quantity - 1) }))} sx={{ border: 1, borderColor: 'divider' }}>
                <Remove fontSize="small" />
            </IconButton>
            <Typography variant="h6" fontWeight="bold" sx={{ minWidth: 32, textAlign: 'center' }}>{value}</Typography>
            <IconButton size="small" onClick={() => setForm(d => ({ ...d, f_quantity: d.f_quantity + 1 }))} sx={{ border: 1, borderColor: 'divider' }}>
                <Add fontSize="small" />
            </IconButton>
        </Box>
    );

    return (
        <Box>
            {notice && <Alert severity="success" sx={{ mt: 0.75 }} onClose={() => setNotice('')}>{notice}</Alert>}
            {/* Current plans */}
            {subscriptions.length === 0 ? (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    No active plans
                </Typography>
            ) : (
                <Box sx={{ mt: 0.75 }}>
                    {subscriptions.map(log => (
                        <Box key={log.f_id} sx={{ mb: 0.75, p: 0.75, bgcolor: 'grey.50', borderRadius: 1 }}>
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Typography variant="body2" fontWeight="medium" noWrap>
                                        {getProductName(products, log.f_business_product_id)}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {`${itemsText(log.f_quantity, scheduleOf(log.f_business_product_id))} · since ${fmtDayY(log.f_effective_from_date)}`}
                                    </Typography>
                                    {!isActivePlan(log.f_action) && (
                                        <Typography variant="caption" sx={{ display: 'block', color: 'warning.main', fontWeight: 700 }}>
                                            {log.scheduledResume
                                                ? `⏸ Paused till ${fmtDay(log.scheduledResume.f_effective_from_date)}`
                                                : '⏸ Paused — until you restart it'}
                                        </Typography>
                                    )}
                                    {log.scheduledPause && (
                                        <Typography variant="caption" sx={{ display: 'block', color: 'warning.main', fontWeight: 700 }}>
                                            {log.scheduledPause.resumeLog
                                                ? `⏸ Pausing ${fmtDay(log.scheduledPause.pauseLog.f_effective_from_date)} → ${fmtDay(log.scheduledPause.resumeLog.f_effective_from_date)}`
                                                : `⏸ Pausing ${fmtDay(log.scheduledPause.pauseLog.f_effective_from_date)}`}
                                        </Typography>
                                    )}
                                </Box>
                                <Chip label={isActivePlan(log.f_action) ? 'Active' : 'Paused'}
                                    color={isActivePlan(log.f_action) ? 'success' : 'warning'}
                                    size="small" sx={{ height: 18, fontSize: '0.65rem', ml: 1 }} />
                            </Box>
                            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                                {isActivePlan(log.f_action) ? (
                                    log.scheduledPause ? (
                                        <Chip icon={<Edit sx={{ fontSize: 14 }} />} label={`Pausing ${fmtDay(log.scheduledPause.pauseLog.f_effective_from_date)}`} size="small" variant="outlined" color="warning" onClick={() => openPauseEdit(log.scheduledPause)} sx={chipSx} />
                                    ) : (
                                        <Chip icon={<Pause sx={{ fontSize: 14 }} />} label="Pause" size="small" variant="outlined" color="warning" onClick={() => openAction('pause', log)} sx={chipSx} />
                                    )
                                ) : (
                                    <Chip icon={<PlayArrow sx={{ fontSize: 14 }} />} label={log.scheduledResume ? 'Restart now' : 'Restart'} size="small" variant="outlined" color="success" onClick={() => openAction('resume', log)} sx={chipSx} />
                                )}
                                {isActivePlan(log.f_action) && (
                                    <Chip icon={<Update sx={{ fontSize: 14 }} />} label="Change Qty" size="small" variant="outlined" color="primary" onClick={() => openAction('changeQty', log)} sx={chipSx} />
                                )}
                                {log.scheduledEnd ? (
                                    <Chip icon={<Edit sx={{ fontSize: 14 }} />} label={`Ending ${fmtDay(log.scheduledEnd.f_effective_from_date)}`} size="small" variant="outlined" color="error" onClick={() => openEndEdit(log)} sx={chipSx} />
                                ) : (
                                    <Chip icon={<Stop sx={{ fontSize: 14 }} />} label="End" size="small" variant="outlined" color="error" onClick={() => openAction('end', log)} sx={chipSx} />
                                )}
                            </Box>
                            {historyLink(log.f_business_product_id)}
                            {historyBody(log.f_business_product_id)}
                        </Box>
                    ))}
                </Box>
            )}

            {/* Products this customer used to take — their history stays reachable */}
            {earlierIds.length > 0 && (
                <Box sx={{ mt: 0.5 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={700}
                        sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                        Earlier plans
                    </Typography>
                    {earlierIds.map(pid => (
                        <Box key={pid} sx={{ mt: 0.5, p: 0.75, borderRadius: 1, border: '1px dashed', borderColor: 'divider' }}>
                            <Typography variant="body2" color="text.secondary" fontWeight="medium" noWrap>
                                {getProductName(products, pid)}
                            </Typography>
                            {historyLink(pid)}
                            {historyBody(pid)}
                        </Box>
                    ))}
                </Box>
            )}

            {/* ═══ Edit list: the individual changes behind one product's dates ═══ */}
            <Dialog open={fixProductId !== null} onClose={() => { setFixProductId(null); setFixError(''); }} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ ...DIALOG_TITLE_SX, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                    Edit — {fixProductId !== null ? getProductName(products, fixProductId) : ''}
                    <IconButton onClick={() => setFixProductId(null)} aria-label="Close">
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ px: 1.5 }}>
                    {fixError && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setFixError('')}>{fixError}</Alert>}
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        Every change made to this plan, newest first. Tap ✏️ to change a date or number of items, or 🗑 to remove a change made by mistake.
                    </Typography>
                    {fixItems.map(item => {
                        if (item.kind === 'pause') {
                            const { pauseLog, resumeLog } = item;
                            return (
                                <Box key={pauseLog.f_id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.75, px: 1, borderLeft: '3px solid', borderColor: 'warning.main', mb: 0.5, bgcolor: 'grey.50', borderRadius: '0 4px 4px 0' }}>
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Typography variant="body2" fontWeight="bold" color="warning.main">Paused</Typography>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            {resumeLog
                                                ? `${fmtDayY(pauseLog.f_effective_from_date)}, restarted ${fmtDayY(resumeLog.f_effective_from_date)}`
                                                : `from ${fmtDayY(pauseLog.f_effective_from_date)}, until restarted`}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }}>
                                        <Tooltip title="Change"><IconButton size="small" color="primary" onClick={() => { setFixProductId(null); openPauseEdit(item); }}><Edit sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                                        <Tooltip title="Delete"><IconButton size="small" color="error"
                                            onClick={() => deleteFromList([resumeLog?.f_id, pauseLog.f_id].filter(Boolean),
                                                resumeLog ? 'Delete this pause and its scheduled resume?' : 'Delete this pause?')}>
                                            <Delete sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                                    </Box>
                                </Box>
                            );
                        }
                        const log = item.log;
                        const label = {
                            'Start': `Started: ${itemsText(log.f_quantity, scheduleOf(log.f_business_product_id))}`,
                            'Resume': `Restarted: ${itemsText(log.f_quantity, scheduleOf(log.f_business_product_id))}`,
                            'Suspend': 'Paused',
                            'End': 'Stopped (last delivery)',
                            'Change in Quantity': `Changed to ${itemsText(log.f_quantity, scheduleOf(log.f_business_product_id))}`,
                        }[log.f_action] || log.f_action;
                        return (
                            <Box key={log.f_id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.75, px: 1, borderLeft: '3px solid', borderColor: getActionColor(log.f_action) + '.main', mb: 0.5, bgcolor: 'grey.50', borderRadius: '0 4px 4px 0' }}>
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Typography variant="body2" fontWeight="bold" color={getActionColor(log.f_action) + '.main'}>{label}</Typography>
                                    <Typography variant="caption" color="text.secondary" display="block">{fmtDayY(log.f_effective_from_date)}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }}>
                                    <Tooltip title="Change"><IconButton size="small" color="primary" onClick={() => openEdit(log)}><Edit sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                                    <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => deleteFromList([log.f_id], 'Delete this change?')}><Delete sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                                </Box>
                            </Box>
                        );
                    })}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setFixProductId(null)}>Done</Button>
                </DialogActions>
            </Dialog>

            {/* ═══ Add plans: start date, then every plan-able product by category ═══ */}
            <AddPlansDialog
                open={addOpen}
                businessId={businessId}
                customer={customer}
                products={products}
                takenIds={currentIds}
                onClose={() => setAddOpen(false)}
                onAdded={handleAdded}
            />

            {/* ═══ Pause / Resume / End / Change Qty ═══ */}
            <Dialog open={actionDialog.open} onClose={closeAction} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ ...DIALOG_TITLE_SX, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                    {actionDialog.type === 'pause'
                        ? (actionDialog.editIds ? 'Edit Pause' : 'Pause Plan')
                        : (actionDialog.type === 'end' && actionDialog.editEndId)
                            ? 'Change end date'
                            : `${ACTION_LABEL[actionDialog.type] || ''} Plan`}
                    <IconButton onClick={closeAction}><Close /></IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        {actionDialog.type === 'changeQty' && (
                            <Grid item xs={12}>
                                <Typography variant="caption" color="text.secondary">New number of items per day</Typography>
                                {qtyButtons(setActionForm, actionForm.f_quantity)}
                            </Grid>
                        )}
                        <Grid item xs={12}>
                            <TextField fullWidth required type="date"
                                label={actionDialog.type === 'pause' ? 'Pause from' : actionDialog.type === 'end' ? 'Last delivery on' : 'From'}
                                value={actionForm.f_effective_from_date || ''}
                                onChange={e => setActionForm(d => ({
                                    ...d,
                                    f_effective_from_date: e.target.value,
                                    endDate: (d.endDate && d.endDate <= e.target.value) ? addDaysISO(e.target.value, 2) : d.endDate,
                                }))}
                                InputLabelProps={{ shrink: true }}
                                inputProps={actionDialog.type === 'pause' ? {} : { min: actionDialog.log?.f_effective_from_date || '' }} />
                        </Grid>
                        {actionDialog.type === 'pause' && (
                            <Grid item xs={12}>
                                <TextField select fullWidth size="small" label="How long?" value={actionForm.duration || 'forever'}
                                    onChange={e => setActionForm(d => ({
                                        ...d, duration: e.target.value,
                                        endDate: e.target.value === 'until' && !d.endDate ? addDaysISO(d.f_effective_from_date, 2) : d.endDate,
                                    }))}>
                                    {[1, 2, 3, 4, 5, 6, 7].map(n => (
                                        <MenuItem key={n} value={String(n)}>{n === 1 ? '1 day' : `${n} days`}</MenuItem>
                                    ))}
                                    <MenuItem value="until">Until a date</MenuItem>
                                    <MenuItem value="forever">Until I restart it</MenuItem>
                                </TextField>
                                {actionForm.duration !== 'until' && actionForm.duration !== 'forever' && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                        Restarts on {fmtDay(addDaysISO(actionForm.f_effective_from_date, Number(actionForm.duration)))}
                                    </Typography>
                                )}
                                {actionForm.duration === 'until' && (
                                    <TextField label="Restart on" type="date" fullWidth size="small" value={actionForm.endDate}
                                        onChange={e => setActionForm(d => ({ ...d, endDate: e.target.value }))}
                                        InputLabelProps={{ shrink: true }}
                                        inputProps={{ min: addDaysISO(actionForm.f_effective_from_date, 1) }} sx={{ mt: 1.5 }} />
                                )}
                                {actionForm.duration === 'forever' && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                        Paused until you restart it.
                                    </Typography>
                                )}
                            </Grid>
                        )}
                    </Grid>
                    {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
                    {success && <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert>}
                </DialogContent>
                <DialogActions>
                    {actionDialog.editEndId && (
                        <Button onClick={() => removeEntries([actionDialog.editEndId], 'Scheduled end removed — the plan will continue.')}
                            variant="outlined" color="success" disabled={busy} sx={{ mr: 'auto', textTransform: 'none', fontWeight: 700 }}>
                            Keep Plan
                        </Button>
                    )}
                    {actionDialog.type === 'pause' && actionDialog.editIds && (
                        <Button onClick={() => removeEntries(actionDialog.editIds, 'Scheduled pause removed — the plan keeps running.')}
                            variant="outlined" color="success" disabled={busy} sx={{ mr: 'auto', textTransform: 'none', fontWeight: 700 }}>
                            Keep Running
                        </Button>
                    )}
                    <Button onClick={closeAction}>Cancel</Button>
                    <Button onClick={handleAction} variant="contained" disabled={busy}
                        color={actionDialog.type === 'end' ? 'error' : actionDialog.type === 'pause' ? 'warning' : 'primary'}>
                        {busy ? <CircularProgress size={24} /> : (
                            actionDialog.type === 'pause'
                                ? (actionDialog.editIds ? 'Save' : 'Pause')
                                : (actionDialog.type === 'end' && actionDialog.editEndId)
                                    ? 'Save'
                                    : (ACTION_LABEL[actionDialog.type] || 'Confirm'))}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ═══ Edit one change ═══ */}
            <Dialog open={!!editingLog} onClose={() => setEditingLog(null)} maxWidth="xs" fullWidth>
                {editingLog && (
                    <>
                        <DialogTitle sx={{ ...DIALOG_TITLE_SX, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                            Edit change
                            <IconButton onClick={() => setEditingLog(null)}><Close /></IconButton>
                        </DialogTitle>
                        <DialogContent dividers>
                            <Grid container spacing={2}>
                                {/* What happened is fixed — delete and add the right entry to change it */}
                                <Grid item xs={12}>
                                    <Typography variant="caption" color="text.secondary">Action</Typography>
                                    <Typography variant="body1" fontWeight={700}>
                                        {ACTION_DISPLAY[editingLog.f_action] || editingLog.f_action}
                                    </Typography>
                                </Grid>
                                {QTY_ACTIONS.includes(editingLog.f_action) && (
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary">Items per day</Typography>
                                        {qtyButtons(setEditForm, editForm.f_quantity)}
                                    </Grid>
                                )}
                                <Grid item xs={6}>
                                    <TextField fullWidth required type="date" label="Date" value={editForm.f_effective_from_date}
                                        onChange={e => setEditForm({ ...editForm, f_effective_from_date: e.target.value })}
                                        InputLabelProps={{ shrink: true }} />
                                </Grid>
                            </Grid>
                            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
                            {success && <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert>}
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setEditingLog(null)}>Cancel</Button>
                            <Button onClick={handleEdit} variant="contained" disabled={busy}>
                                {busy ? <CircularProgress size={24} /> : 'Save'}
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>
        </Box>
    );
});

export default React.memo(CustomerPlans);

/**
 * The Customers tab's version: loads one customer's plan entries itself and shows the same
 * CustomerPlans, with an Add plan button (the Plans page has its + in the card header instead).
 */
export function CustomerPlansForCustomer({ businessId, customer, products, onChanged }) {
    const [logs, setLogs] = useState(null);
    const [loadError, setLoadError] = useState('');
    const plansRef = React.useRef(null);
    const load = useCallback(async () => {
        if (!businessId || !customer?.f_id) return;
        try {
            const res = await businessAPI.getCustomerSubscriptions(businessId, customer.f_id, null, null, false);
            setLogs(res.data || []);
            setLoadError('');
        } catch (err) {
            setLoadError(formatError(err));
            setLogs([]);
        }
    }, [businessId, customer?.f_id]);
    useEffect(() => { load(); }, [load]);
    const changed = useCallback(async () => { await load(); if (onChanged) onChanged(); }, [load, onChanged]);

    if (logs === null) return <Box display="flex" justifyContent="center" p={3}><CircularProgress /></Box>;
    return (
        <Box>
            {loadError && <Alert severity="error" sx={{ mb: 1 }}>{loadError}</Alert>}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button size="small" variant="contained" disableElevation startIcon={<Add />}
                    onClick={() => plansRef.current?.openAdd()}
                    sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 5 }}>
                    Add plan
                </Button>
            </Box>
            <CustomerPlans ref={plansRef} businessId={businessId} customer={customer}
                logs={logs} products={products} onChanged={changed} />
        </Box>
    );
}
